import React, { useState, useMemo } from 'react';
import { Colors as C } from '../../constants/Colors.js';
import { supabase } from '../../lib/supabase.js';
import { mapListing } from '../../lib/listingsService.js';
import WebTopBar from '../components/WebTopBar.js';
import Icon from '../components/Icon.js';
import VinylCover from '../components/VinylCover.js';
import { DCard, Badge, Avatar } from '../components/MarketplaceAtoms.js';
import SurcosRating from '../components/SurcosRating.js';
import PartnerRoleBadges from '../components/PartnerRoleBadges.js';
import PartnerVerifiedBadge from '../components/PartnerVerifiedBadge.js';
import { isDreamvinylVerifiedPartner } from '../utils/partnerVerified.js';
import { fetchReceivedReviews } from '../../lib/reviewsService.js';
import { buildProfilePublicLocationLine } from '../../lib/profileLocationPublic.js';
import { collectProfileSocialEntries } from '../../lib/profileSocialLinks.js';
import { profileLevelLabel } from '../profileConstants.js';
import CosmeticBadgesRow from '../components/CosmeticBadgesRow.js';
import { fetchProfileCosmeticBadges } from '../../lib/groovesSpendService.js';
import { useAppReconnectEffect } from '../../lib/appSessionBridge.js';
import { runAppDataFetch, DEFAULT_APP_FETCH_MS } from '../../lib/appDataFetch.js';

function embedReviewer(r) {
  const rev = Array.isArray(r.reviewer) ? r.reviewer[0] : r.reviewer;
  return rev ?? {};
}

function initialsFromName(name) {
  const s = String(name || '').trim();
  if (!s) return '?';
  const parts = s.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return s.slice(0, 2).toUpperCase();
}

function ratingHistogram(rows) {
  const counts = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
  for (const r of rows) {
    const n = Math.min(5, Math.max(1, Math.round(Number(r.rating) || 0)));
    if (n >= 1 && n <= 5) counts[n] += 1;
  }
  const total = rows.length || 1;
  return [5, 4, 3, 2, 1].map(star => ({
    star,
    pct: Math.round((counts[star] / total) * 100),
  }));
}

function aggregateReviewTags(rows) {
  const freq = new Map();
  for (const r of rows) {
    const tags = Array.isArray(r.tags) ? r.tags : [];
    for (const t of tags) {
      const k = String(t || '').trim();
      if (!k) continue;
      freq.set(k, (freq.get(k) || 0) + 1);
    }
  }
  return [...freq.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, 6)
    .map(([t]) => t);
}

/**
 * Perfil público de otro vendedor: anuncios activos y estadísticas desde Supabase.
 * @param {string} seller — username (sin @)
 * @param {string|null} sellerUserId — UUID si se conoce (p. ej. desde ficha o ranking)
 */
export default function PagePerfilVendedor({ seller, sellerUserId, onBack, onNavigate, onChat, user, initialAvatarUrl = null }) {
  const [tab, setTab] = useState(0);
  const tabs = ['En venta', 'Colección', 'Joyas', 'Ofertas'];

  const [resolvedId, setResolvedId] = useState(null);
  const [profile, setProfile] = useState(null);
  const [sellerListings, setSellerListings] = useState([]);
  const [sellerColeccion, setSellerColeccion] = useState([]);
  const [coleccionLoadErr, setColeccionLoadErr] = useState('');
  const [stats, setStats] = useState({ ventas: 0, compras: 0, seguidores: 0 });
  const [statsLoading, setStatsLoading] = useState(true);
  const [rating, setRating] = useState({ avg: null, count: 0 });
  const [reviewRows, setReviewRows] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [loadErr, setLoadErr] = useState('');
  const [following, setFollowing] = useState(false);
  const [followBusy, setFollowBusy] = useState(false);

  const handle = seller ? `@${String(seller).replace(/^@/, '')}` : '@';

  // ── REPORTAR PERFIL ───────────────
  const [reportOpen, setReportOpen] = useState(false);
  const [reportCategory, setReportCategory] = useState('behavior');
  const [reportBody, setReportBody] = useState('');
  const [reportBusy, setReportBusy] = useState(false);
  const [reportFlash, setReportFlash] = useState('');
  const [cosmeticBadgesEquipped, setCosmeticBadgesEquipped] = useState([]);

  useAppReconnectEffect(() => {
    let cancelled = false;

    const run = async () => {
      const username = String(seller || '')
        .trim()
        .replace(/^@/, '');
      if (!username) {
        setLoadErr('Vendedor no indicado.');
        setStatsLoading(false);
        setListLoading(false);
        return;
      }

      setLoadErr('');
      setStatsLoading(true);
      setListLoading(true);

      let id = sellerUserId || null;
      if (!id) {
        const { data: row, error: e1 } = await supabase.from('profiles').select('id').eq('username', username).maybeSingle();
        if (e1) console.warn('[PagePerfilVendedor] resolve username:', e1.message);
        id = row?.id ?? null;
      }

      if (!id) {
        if (!cancelled) {
          setResolvedId(null);
          setProfile(null);
          setSellerListings([]);
          setSellerColeccion([]);
          setColeccionLoadErr('');
          setStats({ ventas: 0, compras: 0, seguidores: 0 });
          setRating({ avg: null, count: 0 });
          setReviewRows([]);
          setLoadErr('No encontramos ese usuario.');
          setStatsLoading(false);
          setListLoading(false);
        }
        return;
      }

      if (!cancelled) setResolvedId(id);

      try {
        await runAppDataFetch(async () => {
        const [profRes, listRes, salesRes, buysRes, folRes, revRes, followRes] = await Promise.all([
          supabase
            .from('profiles')
            .select(
              'username, display_name, avatar_url, is_verified, is_store, level, location, pais, ciudad, localidad, public_show_pais, public_show_ciudad, public_show_localidad, public_show_coleccion, public_show_joyas, public_show_ofertas, public_show_phone, public_social_visible, phone, phone_verified_at, bio, website_url, social_links, instagram_url, twitter_url, other_social_url, partner_active_roles'
            )
            .eq('id', id)
            .maybeSingle(),
          supabase
            .from('listings')
            .select(
              `
            *,
            profiles ( username, avatar_url, is_verified, is_store, partner_active_roles )
          `
            )
            .eq('seller_id', id)
            .eq('status', 'active')
            .order('created_at', { ascending: false })
            .limit(60),
          supabase.from('transactions').select('id', { count: 'exact', head: true }).eq('seller_id', id),
          supabase.from('transactions').select('id', { count: 'exact', head: true }).eq('buyer_id', id),
          supabase.from('follows').select('follower_id', { count: 'exact', head: true }).eq('following_id', id),
          (async () => {
            const r = await fetchReceivedReviews(id, { onlyVisible: true, limit: 300 });
            return { data: r.data, error: r.error ? { message: r.error } : null };
          })(),
          user?.id
            ? supabase.from('follows').select('follower_id').eq('follower_id', user.id).eq('following_id', id).maybeSingle()
            : Promise.resolve({ data: null, error: null }),
        ]);

        if (cancelled) return;

        setProfile(profRes.data ?? null);
        if (profRes.error) console.warn('[PagePerfilVendedor] profile:', profRes.error.message);

        const rows = listRes.error ? [] : listRes.data ?? [];
        setSellerListings(rows.map(mapListing));
        if (listRes.error) console.warn('[PagePerfilVendedor] listings:', listRes.error.message);

        const profileRow = profRes.data ?? null;
        const wantsAnyColeccion =
          !!profileRow &&
          (profileRow.public_show_coleccion !== false ||
            profileRow.public_show_joyas !== false ||
            profileRow.public_show_ofertas !== false);
        if (wantsAnyColeccion) {
          const { data: colRows, error: colErr } = await supabase
            .from('profile_coleccion_items')
            .select('id,title,artist,cover_url,is_joya,open_to_offers,condition_code')
            .eq('user_id', id)
            .order('created_at', { ascending: false })
            .limit(120);
          if (colErr) {
            console.warn('[PagePerfilVendedor] coleccion:', colErr.message);
            setSellerColeccion([]);
            setColeccionLoadErr('No se pudo cargar la colección pública.');
          } else {
            setSellerColeccion(colRows ?? []);
            setColeccionLoadErr('');
          }
        } else {
          setSellerColeccion([]);
          setColeccionLoadErr('');
        }

        setStats({
          ventas: salesRes.error ? 0 : Number(salesRes.count ?? 0),
          compras: buysRes.error ? 0 : Number(buysRes.count ?? 0),
          seguidores: folRes.error ? 0 : Number(folRes.count ?? 0),
        });

        const revData = revRes.error ? [] : Array.isArray(revRes.data) ? revRes.data : [];
        setReviewRows(revData);
        const count = revData.length;
        const avg = count ? revData.reduce((s, r) => s + (Number(r.rating) || 0), 0) / count : null;
        setRating({ avg, count });

        setFollowing(!!followRes.data && !followRes.error);

        const cosBadges = await fetchProfileCosmeticBadges(id);
        if (!cancelled) setCosmeticBadgesEquipped(cosBadges.equipped ?? []);
        }, { timeoutMs: DEFAULT_APP_FETCH_MS, timeoutMessage: 'Tiempo de espera al cargar el perfil del vendedor.' });
      } catch (e) {
        console.error('[PagePerfilVendedor]', e);
        if (!cancelled) {
          setLoadErr('No se pudo cargar el perfil.');
          setSellerListings([]);
          setSellerColeccion([]);
          setColeccionLoadErr('');
        }
      } finally {
        if (!cancelled) {
          setStatsLoading(false);
          setListLoading(false);
        }
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [seller, sellerUserId, user?.id]);

  const displayName =
    profile?.display_name?.trim() || profile?.username || String(seller || '').replace(/^@/, '') || 'Vendedor';
  const locationLine = buildProfilePublicLocationLine(profile);
  const level = profile ? Math.min(5, Math.max(1, Number(profile.level) || 1)) : 1;

  const valoracionMedia = rating.avg != null ? Math.round(rating.avg * 10) / 10 : null;
  const valoracionSurcos =
    rating.count > 0 && valoracionMedia != null ? Math.min(5, Math.max(1, Math.round(valoracionMedia))) : 0;

  const statLabel = n => {
    if (statsLoading) return '…';
    return String(Number(n) || 0);
  };

  const statRating = () => {
    if (statsLoading) return '…';
    if (rating.count === 0 || valoracionMedia == null) return '—';
    return String(valoracionMedia);
  };

  const histogram = useMemo(() => ratingHistogram(reviewRows), [reviewRows]);
  const topTags = useMemo(() => aggregateReviewTags(reviewRows), [reviewRows]);
  const publicReviewCards = useMemo(() => (reviewRows || []).slice(0, 10), [reviewRows]);
  const coleccionItems = useMemo(() => sellerColeccion, [sellerColeccion]);
  const joyasItems = useMemo(() => sellerColeccion.filter(x => !!x.is_joya), [sellerColeccion]);
  const ofertasItems = useMemo(() => sellerColeccion.filter(x => !!x.open_to_offers), [sellerColeccion]);
  const showPublicColeccion = profile?.public_show_coleccion !== false;
  const showPublicJoyas = profile?.public_show_joyas !== false;
  const showPublicOfertas = profile?.public_show_ofertas !== false;

  const toggleFollow = async () => {
    if (!user?.id || !resolvedId || user.id === resolvedId) return;
    setFollowBusy(true);
    try {
      if (following) {
        const { error } = await supabase.from('follows').delete().eq('follower_id', user.id).eq('following_id', resolvedId);
        if (!error) setFollowing(false);
        else console.warn('[PagePerfilVendedor] unfollow:', error.message);
      } else {
        const { error } = await supabase.from('follows').insert({ follower_id: user.id, following_id: resolvedId });
        if (!error) setFollowing(true);
        else console.warn('[PagePerfilVendedor] follow:', error.message);
      }
    } finally {
      setFollowBusy(false);
    }
  };

  const openReport = () => {
    if (!user?.id || !resolvedId || user.id === resolvedId) return;
    setReportFlash('');
    setReportCategory('behavior');
    setReportBody('');
    setReportOpen(true);
  };

  const submitReport = async () => {
    if (!user?.id || !resolvedId || user.id === resolvedId) return;
    if (reportBusy) return;
    if (String(reportBody || '').trim().length < 6) {
      setReportFlash('Cuéntanos un poco más (mínimo 6 caracteres).');
      return;
    }
    setReportBusy(true);
    setReportFlash('');
    try {
      const { error } = await supabase.from('user_reports').insert({
        reporter_id: user.id,
        target_user_id: resolvedId,
        category: reportCategory,
        body: String(reportBody || '').trim(),
      });
      if (error) throw error;
      setReportFlash('Reporte recibido correctamente. También te lo enviamos a Notificaciones.');
      setTimeout(() => setReportOpen(false), 700);
    } catch (e) {
      setReportFlash('No se pudo enviar el reporte. Inténtalo de nuevo.');
    } finally {
      setReportBusy(false);
    }
  };

  return (
    <div style={{ background: C.dark, minHeight: '100vh', color: '#fff' }}>
      <WebTopBar onBack={onBack} title={handle} mini dark user={user} onNavigate={onNavigate} onSignOut={onBack} />
      <div style={{ maxWidth: 800, margin: '0 auto', padding: '24px 20px 60px' }}>
        {loadErr ? (
          <DCard style={{ padding: 16, marginBottom: 16 }}>
            <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.75)' }}>{loadErr}</div>
          </DCard>
        ) : null}

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, marginBottom: 20 }}>
          <Avatar
            initials={initialsFromName(displayName)}
            bg="#534AB7"
            color="#EEEDFE"
            size={64}
            imageUrl={profile?.avatar_url ?? initialAvatarUrl ?? null}
          />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 17, fontWeight: 500, color: '#fff' }}>{displayName}</div>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginTop: 2 }}>
              {handle}
              {locationLine ? ` · ${locationLine}` : ''}
              {profile?.public_show_phone && profile?.phone_verified_at && String(profile?.phone || '').trim()
                ? ` · Tel. ${String(profile.phone).trim()}`
                : ''}
            </div>
            <div style={{ display: 'flex', gap: 5, marginTop: 8, flexWrap: 'wrap' }}>
              {isDreamvinylVerifiedPartner({
                isVerified: profile?.is_verified,
                isStore: profile?.is_store,
                partnerRoleKeys: profile?.partner_active_roles,
              }) ? (
                <PartnerVerifiedBadge size={18} />
              ) : null}
              <Badge type="gold">
                {profileLevelLabel(level)} · Nv.{level}
              </Badge>
            </div>
            <PartnerRoleBadges roleKeys={profile?.partner_active_roles} theme="dark" style={{ marginTop: 8 }} />
            <CosmeticBadgesRow badges={cosmeticBadgesEquipped} style={{ marginTop: 6 }} />
            {profile?.bio?.trim() ? (
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginTop: 8, lineHeight: 1.45 }}>{profile.bio.trim()}</div>
            ) : null}
            {(() => {
              const links = collectProfileSocialEntries(profile);
              if (!links.length) return null;
              return (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 12px', marginTop: 8, alignItems: 'center' }}>
                  {links.map(([label, url]) => (
                    <a
                      key={label}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ fontSize: 10, color: C.gold, textDecoration: 'underline' }}>
                      {label}
                    </a>
                  ))}
                </div>
              );
            })()}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={() =>
                onChat &&
                onChat({
                  sellerUsername: profile?.username || seller,
                  sellerUserId: resolvedId || null,
                  listingId: null,
                  listingTitle: null,
                  listingPrice: null,
                  sellerAvatarUrl: profile?.avatar_url ?? null,
                  isAuction: false,
                })
              }
              style={{
                padding: '8px 14px',
                borderRadius: 10,
                background: 'transparent',
                border: `0.5px solid rgba(255,255,255,0.2)`,
                color: 'rgba(255,255,255,0.7)',
                fontSize: 12,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 5,
              }}
            >
              <Icon name="chat" size={13} color="rgba(255,255,255,0.7)" /> Mensaje
            </button>
            {user?.id && resolvedId && user.id !== resolvedId ? (
              <button
                disabled={followBusy}
                onClick={() => toggleFollow()}
                style={{
                  padding: '8px 16px',
                  borderRadius: 10,
                  background: following ? 'transparent' : C.gold,
                  border: following ? `0.5px solid rgba(255,255,255,0.2)` : 'none',
                  color: following ? 'rgba(255,255,255,0.6)' : '#0a0a0f',
                  fontSize: 12,
                  fontWeight: following ? 500 : 600,
                  cursor: followBusy ? 'wait' : 'pointer',
                  opacity: followBusy ? 0.7 : 1,
                }}
              >
                {following ? 'Siguiendo' : 'Seguir'}
              </button>
            ) : null}
            {user?.id && resolvedId && user.id !== resolvedId ? (
              <button
                disabled={reportBusy}
                onClick={() => openReport()}
                style={{
                  padding: '8px 12px',
                  borderRadius: 10,
                  background: 'rgba(255,255,255,0.04)',
                  border: `0.5px solid rgba(255,255,255,0.14)`,
                  color: 'rgba(255,255,255,0.55)',
                  fontSize: 12,
                  cursor: reportBusy ? 'wait' : 'pointer',
                  fontWeight: 600,
                }}
              >
                Reportar
              </button>
            ) : null}
          </div>
        </div>

        {reportOpen ? (
          <div
            onClick={() => { if (!reportBusy) setReportOpen(false); }}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.62)',
              display: 'grid',
              placeItems: 'center',
              zIndex: 60,
              padding: 14,
            }}
          >
            <div
              onClick={e => e.stopPropagation()}
              style={{
                width: 'min(520px, 96vw)',
                background: '#fff',
                borderRadius: 12,
                border: '1px solid rgba(0,0,0,0.12)',
                padding: 16,
                boxShadow: '0 20px 80px rgba(0,0,0,0.35)',
                color: '#111',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 900, fontSize: '0.98rem' }}>Reportar perfil</div>
                  <div style={{ fontSize: '0.8rem', color: '#777', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {handle}
                  </div>
                </div>
                <button
                  type="button"
                  disabled={reportBusy}
                  onClick={() => setReportOpen(false)}
                  style={{ background: 'transparent', border: '1px solid rgba(0,0,0,0.12)', padding: '6px 10px', borderRadius: 8, cursor: reportBusy ? 'default' : 'pointer', color: '#666', fontWeight: 800 }}
                >
                  Cerrar
                </button>
              </div>

              <div style={{ display: 'grid', gap: 10, marginTop: 14 }}>
                <div style={{ display: 'grid', gap: 6 }}>
                  <div style={{ fontSize: '0.75rem', color: '#666', fontWeight: 800 }}>Motivo</div>
                  <select
                    value={reportCategory}
                    onChange={e => setReportCategory(e.target.value)}
                    style={{ width: '100%', padding: '10px 12px', borderRadius: 9, border: '1px solid rgba(0,0,0,0.14)', background: '#fafafa', fontWeight: 700 }}
                  >
                    <option value="behavior">Comportamiento inapropiado</option>
                    <option value="spam">Spam</option>
                    <option value="fraud">Posible estafa</option>
                    <option value="bullying">Acoso / insultos</option>
                    <option value="impersonation">Suplantación</option>
                    <option value="other">Otro</option>
                  </select>
                </div>

                <div style={{ display: 'grid', gap: 6 }}>
                  <div style={{ fontSize: '0.75rem', color: '#666', fontWeight: 800 }}>Detalles</div>
                  <textarea
                    value={reportBody}
                    onChange={e => setReportBody(e.target.value)}
                    placeholder="Describe el problema para que podamos revisarlo rápido."
                    rows={4}
                    style={{ width: '100%', padding: '10px 12px', borderRadius: 9, border: '1px solid rgba(0,0,0,0.14)', background: '#fafafa', resize: 'vertical', fontFamily: 'inherit' }}
                  />
                </div>

                {reportFlash ? (
                  <div style={{ fontSize: '0.78rem', color: reportFlash.includes('Gracias') ? C.green : C.warn, fontWeight: 800 }}>
                    {reportFlash}
                  </div>
                ) : null}

                <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 2 }}>
                  <button
                    type="button"
                    disabled={reportBusy}
                    onClick={() => setReportOpen(false)}
                    style={{ background: 'transparent', border: '1px solid rgba(0,0,0,0.12)', padding: '10px 14px', borderRadius: 10, cursor: reportBusy ? 'default' : 'pointer', color: '#666', fontWeight: 800 }}
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    disabled={reportBusy}
                    onClick={() => void submitReport()}
                    style={{ background: C.gold, color: '#000', border: 'none', padding: '10px 14px', borderRadius: 10, cursor: reportBusy ? 'default' : 'pointer', fontWeight: 900 }}
                  >
                    {reportBusy ? 'Enviando…' : 'Enviar reporte'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : null}

        {/* Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 8, marginBottom: 16 }}>
          {[
            [statLabel(stats.ventas), 'Ventas'],
            [statLabel(stats.compras), 'Compras'],
            [statLabel(stats.seguidores), 'Seguidores'],
            [statRating(), 'Valoración'],
          ].map(([n, l]) => (
            <DCard key={l} style={{ padding: '10px 8px', textAlign: 'center' }}>
              <div style={{ fontSize: 18, fontWeight: 500, color: C.gold }}>{n}</div>
              <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.4)', marginTop: 2 }}>{l}</div>
            </DCard>
          ))}
        </div>

        {/* Valoraciones resumidas */}
        <DCard style={{ padding: 14, marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 10 }}>
            <div>
              <div style={{ fontSize: 30, fontWeight: 500, color: C.gold, lineHeight: 1 }}>
                {statsLoading ? '…' : rating.count === 0 ? '—' : valoracionMedia}
              </div>
              <div style={{ margin: '6px 0 4px' }}>
                <SurcosRating value={valoracionSurcos} readOnly size="sm" />
              </div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>
                {statsLoading ? '…' : `${rating.count} valoracion${rating.count === 1 ? '' : 'es'}`}
              </div>
            </div>
            <div style={{ flex: 1 }}>
              {histogram.map(({ star, pct }) => (
                <div key={star} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                  <span style={{ fontSize: 8, color: 'rgba(255,255,255,0.4)', width: 8 }}>{star}</span>
                  <div style={{ flex: 1, height: 3, background: 'rgba(255,255,255,0.08)', borderRadius: 2, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${pct}%`, background: C.gold, borderRadius: 2 }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
          {topTags.length ? (
            <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
              {topTags.map(t => (
                <span
                  key={t}
                  style={{
                    fontSize: 9,
                    padding: '2px 8px',
                    borderRadius: 8,
                    background: 'rgba(29,158,117,0.1)',
                    color: C.green,
                    border: '0.5px solid rgba(29,158,117,0.2)',
                  }}
                >
                  {t}
                </span>
              ))}
            </div>
          ) : null}
          {publicReviewCards.length > 0 ? (
            <div style={{ marginTop: 12, borderTop: '0.5px solid rgba(255,255,255,0.08)', paddingTop: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.55)', marginBottom: 10 }}>Últimas valoraciones</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {publicReviewCards.map(r => {
                  const rev = embedReviewer(r);
                  const uname = rev.username ?? 'Usuario';
                  const sur = Math.min(5, Math.max(1, Math.round(Number(r.rating) || 1)));
                  const tgs = Array.isArray(r.tags) ? r.tags : [];
                  return (
                    <div key={r.id} style={{ padding: 10, borderRadius: 10, background: 'rgba(255,255,255,0.04)', border: '0.5px solid rgba(255,255,255,0.08)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 6 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                          <Avatar
                            initials={initialsFromName(uname)}
                            size={28}
                            bg="#534AB7"
                            color="#EEEDFE"
                            imageUrl={rev.avatar_url}
                          />
                          <span style={{ fontSize: 11, fontWeight: 600, color: '#fff' }}>@{uname}</span>
                        </div>
                        <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.35)' }}>
                          {r.created_at ? new Date(r.created_at).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' }) : ''}
                        </span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                        <SurcosRating value={sur} readOnly size="sm" />
                      </div>
                      {tgs.length ? (
                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 6 }}>
                          {tgs.map(t => (
                            <span key={String(t)} style={{ fontSize: 8, padding: '2px 6px', borderRadius: 6, background: 'rgba(29,158,117,0.12)', color: C.green }}>
                              {t}
                            </span>
                          ))}
                        </div>
                      ) : null}
                      {r.comment?.trim() ? (
                        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.65)', lineHeight: 1.45 }}>{r.comment.trim()}</div>
                      ) : null}
                      {r.seller_public_reply?.trim() ? (
                        <div
                          style={{
                            marginTop: 8,
                            padding: '8px 10px',
                            borderRadius: 8,
                            background: 'rgba(200,168,75,0.08)',
                            border: '0.5px solid rgba(200,168,75,0.22)',
                          }}
                        >
                          <div style={{ fontSize: 9, color: C.gold, fontWeight: 700, marginBottom: 4 }}>Respuesta pública del vendedor</div>
                          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.78)', lineHeight: 1.45 }}>{r.seller_public_reply.trim()}</div>
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </div>
          ) : null}
          <button
            onClick={() => onNavigate('valoraciones')}
            style={{
              marginTop: 10,
              width: '100%',
              padding: '9px',
              borderRadius: 10,
              background: 'transparent',
              border: '0.5px solid rgba(200,168,75,0.25)',
              color: C.gold,
              fontSize: 11,
              cursor: 'pointer',
            }}
          >
            Ver todas las valoraciones →
          </button>
        </DCard>

        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: '0.5px solid rgba(255,255,255,0.1)', marginBottom: 14 }}>
          {tabs.map((t, i) => (
            <button
              key={t}
              onClick={() => setTab(i)}
              style={{
                padding: '10px 0',
                fontSize: 11,
                color: tab === i ? C.gold : 'rgba(255,255,255,0.45)',
                cursor: 'pointer',
                marginRight: 16,
                border: 'none',
                background: 'transparent',
                borderBottom: `2px solid ${tab === i ? C.gold : 'transparent'}`,
                whiteSpace: 'nowrap',
              }}
            >
              {t}
            </button>
          ))}
        </div>

        {tab === 0 && listLoading ? (
          <div style={{ textAlign: 'center', padding: '30px 0', color: 'rgba(255,255,255,0.35)', fontSize: 12 }}>Cargando anuncios…</div>
        ) : tab === 0 && sellerListings.length > 0 ? (
          sellerListings.map(item => (
            <DCard
              key={item.id}
              onClick={() => onNavigate('ficha', item)}
              style={{ display: 'flex', gap: 10, padding: '10px 12px', marginBottom: 8, cursor: 'pointer' }}
            >
              <div style={{ width: 56, height: 56, borderRadius: 8, flexShrink: 0 }}>
                <VinylCover item={item} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 500, color: '#fff' }}>{item.title}</div>
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginTop: 2 }}>
                  {item.artist} · {item.condition}
                </div>
                <PartnerRoleBadges roleKeys={item.sellerPartnerRoleKeys} compact theme="dark" style={{ marginTop: 6 }} />
                <div style={{ fontSize: 14, fontWeight: 500, color: C.gold, marginTop: 4 }}>
                  {item.isAuction ? `${item.currentBid != null ? item.currentBid : item.price} €` : `${item.price} €`}
                </div>
              </div>
            </DCard>
          ))
        ) : tab === 0 ? (
          <div style={{ textAlign: 'center', padding: '30px 0', color: 'rgba(255,255,255,0.25)', fontSize: 12 }}>
            Este vendedor no tiene artículos en venta ahora mismo
          </div>
        ) : null}

        {tab > 0 ? (
          (() => {
            const mapByTab = [[], coleccionItems, joyasItems, ofertasItems];
            const visibleByTab = [true, showPublicColeccion, showPublicJoyas, showPublicOfertas];
            const rows = mapByTab[tab] ?? [];
            if (!visibleByTab[tab]) {
              return (
                <div style={{ textAlign: 'center', padding: '30px 0', color: 'rgba(255,255,255,0.25)', fontSize: 12 }}>
                  Esta sección es privada
                </div>
              );
            }
            if (coleccionLoadErr) {
              return (
                <div style={{ textAlign: 'center', padding: '30px 0', color: 'rgba(255,255,255,0.3)', fontSize: 12 }}>
                  {coleccionLoadErr}
                </div>
              );
            }
            if (rows.length === 0) {
              return (
                <div style={{ textAlign: 'center', padding: '30px 0', color: 'rgba(255,255,255,0.25)', fontSize: 12 }}>
                  Sin elementos en esta sección.
                </div>
              );
            }
            return rows.map(row => (
              <DCard key={row.id} style={{ display: 'flex', gap: 10, padding: '10px 12px', marginBottom: 8, alignItems: 'center' }}>
                <div style={{ width: 48, height: 48, borderRadius: 8, background: 'rgba(255,255,255,0.05)', overflow: 'hidden', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {row.cover_url ? (
                    <img src={row.cover_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <Icon name="disc" size={15} color="rgba(255,255,255,0.4)" />
                  )}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: '#fff' }}>{row.title}</div>
                  <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.42)', marginTop: 2 }}>
                    {[row.artist, row.condition_code].filter(Boolean).join(' · ') || 'Sin detalles'}
                  </div>
                </div>
                {row.is_joya ? <Badge type="gold">Joya</Badge> : null}
                {row.open_to_offers ? <Badge type="green">Ofertas</Badge> : null}
              </DCard>
            ));
          })()
        ) : null}
      </div>
    </div>
  );
}
